import { useLayoutEffect, useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { getGetCurrentUserQueryKey } from '@cdf/api-client-react';
import { useAuth } from '@/hooks/use-auth';
import { useUser } from '@/components/auth/user-provider';
import { useLocalSession } from '@/lib/local-session';
import { registerSessionQueryClient } from '@/lib/session-query-cache';
import { entityCacheIdentity } from '@/features/tenancy/context-navigation';

function newEntityClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false }
    }
  });
}

/** A route or account change gets a fresh data cache before entity pages render. */
export function EntityCacheBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { generation } = useLocalSession();
  const { accountKey } = useAuth();
  const rootClient = useQueryClient();
  const user = useUser();
  const identity = JSON.stringify([generation, entityCacheIdentity(accountKey, pathname), user?.appUserId,
    user?.viewAsRole, user?.capabilities, user?.tenants, user?.groupAccess, user?.isPlatformAdmin]);
  const [scope, setScope] = useState(() => ({ identity, accountKey, client: newEntityClient() }));

  useLayoutEffect(() => registerSessionQueryClient(scope.client), [scope.client]);

  useLayoutEffect(() => {
    if (scope.identity === identity) return;
    void scope.client.cancelQueries();
    scope.client.clear();
    // The outer session observer owns entity-scoped /me refreshes. Removing
    // its active query here can strand it with pre-navigation capabilities.
    // Purge the previous account only; ordinary entity changes retain correctly
    // keyed permissions while the inner data cache remains strictly isolated.
    if (scope.accountKey !== accountKey) {
      rootClient.removeQueries({ queryKey: [...getGetCurrentUserQueryKey(), scope.accountKey] });
    }
    setScope({ identity, accountKey, client: newEntityClient() });
  }, [identity, accountKey, rootClient, scope]);

  if (scope.identity !== identity) return null;
  return <QueryClientProvider client={scope.client}>{children}</QueryClientProvider>;
}
